import './index.less';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { Message, Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import {
    BaseStagePlugin,
    useActiveOriginIndex,
    useActiveStageIndex,
    useFramework,
    useIsLongImageMode,
    useStageListLength,
} from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { checkPosterTypeIsAiPPT } from '@/utils/stage-kit';
import { IExportPlugin, ILayerProps, MAX_STAGES, MAX_STAGES_TIP, PluginType } from '@/version-types';
import { AdjustOperate } from './adjust-operate';
import { QuickStageHistory } from './history/quick-stage-history';
import { QuickAdjustOperate } from './operate/quick-adjust-operate';
import { QuickResizeOperate } from './operate/quick-resize-operate';
import { QuickStageOperate } from './operate/quick-stage-operate';
import { ResizeOperate } from './resize-operate';

const StageOperateView: React.FC = React.memo(() => {
    const framework = useFramework();
    const activeIndex = useActiveStageIndex();
    const originIndex = useActiveOriginIndex();
    const isLongImageMode = useIsLongImageMode();
    const { $t } = useLocaleStore();
    const mouseEvents = isLongImageMode
        ? {
              onMouseEnter: () => {
                  framework.events.emitMouseEnter(framework.stage.getActiveOriginIndex());
              },
              onMouseLeave: () => {
                  framework.events.emitMouseLeave(framework.stage.getActiveOriginIndex());
              },
          }
        : {};

    useEffect(() => {
        const handler = framework.events.onBaseOperate(() => {
            framework.stage.setIsPickStage(false);
        });
        return () => framework.events.off(handler);
    }, []);

    const count = useStageListLength();
    const disableUp = activeIndex === 0;
    const disableDown = activeIndex === count - 1;
    const disableDel = count === 1;

    return (
        <BaseStagePlugin enableMouseEvents={true} originIndexOrLayerId={originIndex} name="stage-operate">
            <>
                {/* 舞台调整尺寸 */}
                {isLongImageMode ? <ResizeOperate /> : <AdjustOperate />}
                {/* 右边快速操作 */}
                <div className="c-quick-operate" {...mouseEvents}>
                    <Tooltip
                        placement="right"
                        title={
                            !isLongImageMode ? $t((v) => v.stageManager.prePage) : $t((v) => v.stageManager.moveUpPage)
                        }
                        overlayClassName="c-quick-operate-tooltip"
                    >
                        <div
                            className={classNames('c-quick-operate__item', { disable: disableUp })}
                            onClick={() => {
                                framework.getInstance(QuickStageOperate).up()?.emitQuickClick('up_canvas');
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <IconFont name={!isLongImageMode ? 'ArrowUpBold' : 'SortUpBold'} fontSize={16} />
                        </div>
                    </Tooltip>

                    <Tooltip
                        placement="right"
                        title={
                            !isLongImageMode
                                ? $t((v) => v.stageManager.nextPage)
                                : $t((v) => v.stageManager.moveDownPage)
                        }
                        overlayClassName="c-quick-operate-tooltip"
                    >
                        <div
                            className={classNames('c-quick-operate__item', {
                                disable: disableDown,
                            })}
                            onClick={() => {
                                framework.getInstance(QuickStageOperate).down()?.emitQuickClick('down_canvas');
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <IconFont name={!isLongImageMode ? 'ArrowDownBold' : 'SortDownBold'} fontSize={16} />
                        </div>
                    </Tooltip>
                    <Tooltip
                        placement="right"
                        title={$t((v) => v.stageManager.addPage)}
                        overlayClassName="c-quick-operate-tooltip"
                    >
                        <div
                            className={classNames('c-quick-operate__item')}
                            onClick={() => {
                                if (framework.getRenderStageLength() < MAX_STAGES) {
                                    const bgLayer = framework.getBackgroundLayer(
                                        framework.stage.getActiveOriginIndex(),
                                    );
                                    const isAiPPT = checkPosterTypeIsAiPPT(framework.getPosterType());
                                    framework
                                        .getInstance(QuickStageOperate)
                                        .create(
                                            framework.stage.getActiveStageIndex(),
                                            undefined,
                                            isAiPPT ? (bgLayer ? bgLayer : undefined) : undefined,
                                            framework.getCustomData<ILayerProps[]>('masterPage'),
                                        )
                                        .scrollToActiveStage()
                                        .setStageSelection()
                                        .emitQuickClick('new_canvas');
                                } else {
                                    Message.warning(MAX_STAGES_TIP());
                                }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <IconFont name="PlusCircleBold" fontSize={16} />
                        </div>
                    </Tooltip>
                    <Tooltip
                        placement="right"
                        title={$t((v) => v.stageManager.copyPage)}
                        overlayClassName="c-quick-operate-tooltip"
                    >
                        <div
                            className={classNames('c-quick-operate__item')}
                            onClick={() => {
                                if (framework.getRenderStageLength() < MAX_STAGES) {
                                    framework
                                        .getInstance(QuickStageOperate)
                                        .clone(framework.stage.getActiveStageIndex())
                                        .scrollToActiveStage()
                                        .setStageSelection()
                                        .emitQuickClick('copy_canvas');
                                } else {
                                    Message.warning(MAX_STAGES_TIP());
                                }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <IconFont name="DuplicateBold" fontSize={16} />
                        </div>
                    </Tooltip>
                    <Tooltip
                        placement="right"
                        title={$t((v) => v.stageManager.delPage)}
                        overlayClassName="c-quick-operate-tooltip"
                    >
                        <div
                            className={classNames('c-quick-operate__item', { disable: disableDel })}
                            onClick={() => {
                                const length = framework.getRenderStageLength();
                                if (length > 1) {
                                    framework
                                        .getInstance(QuickStageOperate)
                                        .emitQuickClick('delete_canvas')
                                        .delete(framework.stage.getActiveStageIndex())
                                        .scrollToActiveStage()
                                        .setStageSelection();
                                }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <IconFont name="TrashCanBold" fontSize={16} />
                        </div>
                    </Tooltip>
                </div>
            </>
        </BaseStagePlugin>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 3,
    name: 'stage-operate',
    createInstance({ identity }) {
        return [
            { key: 'QuickStageOperate', value: new QuickStageOperate(identity) },
            { key: 'QuickAdjustOperate', value: new QuickAdjustOperate(identity) },
            { key: 'QuickResizeOperate', value: new QuickResizeOperate(identity) },
        ];
    },
    createHistory({ identity }) {
        return [new QuickStageHistory(identity)];
    },
    render() {
        return <StageOperateView />;
    },
};

export default exportPlugin;
